import { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html {
    font-size: 16px;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: 14px;
    }
  }
  
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    min-height: 100vh;
    line-height: 1.5;
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: transparent;
    overflow-x: hidden;
  }
  
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
  
  button {
    cursor: pointer;
    background: none;
    border: none;
    font-family: inherit;
  }
  
  ul, ol {
    list-style: none;
  }
  
  input, button, textarea, select {
    font-family: inherit;
    font-size: inherit;
  }
  
  img, video {
    max-width: 100%;
    height: auto;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 0.5rem;
  }
  
  h1 {
    font-size: ${({ theme }) => theme.fontSizes.xxl};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: 1.75rem;
    }
  }
  
  h2 {
    font-size: 1.75rem;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: 1.5rem;
    }
  }
  
  h3 {
    font-size: 1.5rem;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: 1.25rem;
    }
  }
  
  p {
    margin-bottom: 1rem;
  }
  
  /* 스크롤바 스타일링 */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.light};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.secondary};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.primary};
  }
  
  /* 컨테이너 스타일 */
  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${({ theme }) => theme.spacing.md};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding: 0 ${({ theme }) => theme.spacing.sm};
    }
  }
  
  /* 카드 스타일 */
  .card {
    background: ${({ theme }) => theme.colors.cardBg};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border: 1px solid ${({ theme }) => theme.colors.border};
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      border-radius: ${({ theme }) => theme.borderRadius.sm};
    }
  }
  
  .card:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      transform: translateY(-2px);
    }
  }

  /* 버튼 스타일 */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: 500;
    transition: all 0.2s;
    text-align: center;
  }

  .btn-primary {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border: none;
  }

  .btn-primary:hover {
    background-color: ${({ theme }) => theme.colors.danger};
  }

  .btn-outline {
    background-color: transparent;
    border: 1px solid ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  .btn-outline:hover {
    background-color: ${({ theme }) => theme.colors.light};
  }

  /* 그리드 레이아웃 */
  .grid {
    display: grid;
    gap: ${({ theme }) => theme.spacing.md};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      gap: ${({ theme }) => theme.spacing.sm};
    }
  }
  
  .grid-2 {
    grid-template-columns: repeat(2, 1fr);
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      grid-template-columns: 1fr;
    }
  }
  
  .grid-3 {
    grid-template-columns: repeat(3, 1fr);
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      grid-template-columns: 1fr;
    }
  }
  
  .grid-4 {
    grid-template-columns: repeat(4, 1fr);
    
    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
      grid-template-columns: repeat(3, 1fr);
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      grid-template-columns: 1fr;
    }
  }

  /* 배너 아이템 */
  .banner-item {
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing.lg};
    color: white;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding: ${({ theme }) => theme.spacing.md};
      border-radius: ${({ theme }) => theme.borderRadius.sm};
    }
  }

  /* 이스포츠 섹션 */
  .esports-section {
    background-color: ${({ theme }) => theme.colors.esportsBg};
  }

  /* 스포츠 섹션 */
  .sports-section {
    background-color: ${({ theme }) => theme.colors.sportsBg};
  }

  /* 카지노 섹션 */
  .casino-section {
    background-color: ${({ theme }) => theme.colors.casinoBg};
  }
  
  /* 여백 클래스 */
  .mt-1 { margin-top: ${({ theme }) => theme.spacing.sm}; }
  .mt-2 { margin-top: ${({ theme }) => theme.spacing.md}; }
  .mt-3 { margin-top: ${({ theme }) => theme.spacing.lg}; }
  
  .mb-1 { margin-bottom: ${({ theme }) => theme.spacing.sm}; }
  .mb-2 { margin-bottom: ${({ theme }) => theme.spacing.md}; }
  .mb-3 { margin-bottom: ${({ theme }) => theme.spacing.lg}; }
  
  .mx-auto { margin-left: auto; margin-right: auto; }
  
  /* 반응형 유틸리티 */
  .d-none { display: none; }
  .d-block { display: block; }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    .d-md-none { display: none; }
    .d-md-block { display: block; }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    .d-sm-none { display: none; }
    .d-sm-block { display: block; }
  }
  
  /* 텍스트 정렬 */
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .text-right { text-align: right; }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    .text-sm-center { text-align: center; }
  }
  
  /* 여백 조정 */
  .p-0 { padding: 0; }
  .p-1 { padding: ${({ theme }) => theme.spacing.sm}; }
  .p-2 { padding: ${({ theme }) => theme.spacing.md}; }
  .p-3 { padding: ${({ theme }) => theme.spacing.lg}; }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    .p-sm-0 { padding: 0; }
    .p-sm-1 { padding: ${({ theme }) => theme.spacing.xs}; }
    .p-sm-2 { padding: ${({ theme }) => theme.spacing.sm}; }
  }
`;

export default GlobalStyle; 